import { useState } from "react";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, MapPin, Upload, Mic, MessageSquare, ChevronRight, AlertCircle, TrendingUp, Gauge, Zap, Layers, FileText, Camera, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export const DemoPage = ({ onRequestDemo }) => {
  const [view, setView] = useState('start'); // start, analyzing, result, structured
  const [input, setInput] = useState('');
  const [location, setLocation] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Für strukturierten Pfad (7-W im Hintergrund)
  const [structuredStep, setStructuredStep] = useState(0);
  const [selections, setSelections] = useState({
    targetGroup: null, participants: null, bundesland: '', landkreis: '',
    workArea: null, projectType: null, timing: null, condition: null,
    existing: [], pefcRelevant: null, startLevel: null, goals: []
  });

  // KI-Verdichtung simulieren
  const analyzeInput = () => {
    if (!input.trim() && !location.trim()) return;
    
    setIsAnalyzing(true);
    setView('analyzing');
    
    // Simulate AI processing
    setTimeout(() => {
      const result = generateAnalysis(input, location);
      setAnalysisResult(result);
      setIsAnalyzing(false);
      setView('result');
    }, 2000);
  };

  // Intelligente Analyse aus Freitext
  const generateAnalysis = (text, loc) => {
    const lowerText = text.toLowerCase();
    
    // Falltyp erkennen
    let caseType = 'Forstvorhaben';
    let workArea = 'Planung / Organisation';
    if (lowerText.includes('pflanz') || lowerText.includes('kultur')) {
      caseType = 'Pflanzung / Kulturmaßnahme';
      workArea = 'Pflanzung / Kultur';
    } else if (lowerText.includes('jung') || lowerText.includes('pflege') || lowerText.includes('läuter')) {
      caseType = 'Jungbestandspflege';
      workArea = 'Jungbestandspflege';
    } else if (lowerText.includes('durchforst')) {
      caseType = 'Durchforstung';
      workArea = 'Durchforstung';
    } else if (lowerText.includes('ernte') || lowerText.includes('einschlag') || lowerText.includes('holz')) {
      caseType = 'Holzernte';
      workArea = 'Holzernte';
    } else if (lowerText.includes('schad') || lowerText.includes('käfer') || lowerText.includes('sturm')) {
      caseType = 'Waldschutz / Kalamität';
      workArea = 'Waldschutz';
    }

    // Beteiligte erkennen
    let participants = 'Einzelperson';
    if (lowerText.includes('unternehmer') || lowerText.includes('dienstleister') || lowerText.includes('firma')) {
      participants = 'Mit Auftragnehmer';
    } else if (lowerText.includes('förster') || lowerText.includes('berater')) {
      participants = 'Mit fachlicher Begleitung';
    }

    // Dringlichkeit erkennen
    let urgency = 'Normal';
    if (lowerText.includes('dringend') || lowerText.includes('schnell') || lowerText.includes('sofort') || lowerText.includes('akut')) {
      urgency = 'Erhöht';
    }

    // PEFC erkennen
    let pefcRelevant = false;
    if (lowerText.includes('pefc') || lowerText.includes('zertifiz') || lowerText.includes('nachweis')) {
      pefcRelevant = true;
    }

    // Offene Punkte ableiten
    const gaps = [];
    if (!loc) gaps.push('Genauer Standort / Flächenbezug');
    if (!lowerText.includes('ha') && !lowerText.includes('hektar') && !lowerText.includes('fläche')) {
      gaps.push('Flächengröße');
    }
    if (!lowerText.includes('termin') && !lowerText.includes('wann') && !lowerText.includes('bis')) {
      gaps.push('Zeitrahmen / Termin');
    }
    if (participants === 'Einzelperson') {
      gaps.push('Beteiligte / Zuständigkeiten');
    }
    if (!pefcRelevant) {
      gaps.push('PEFC-/Zertifizierungsrelevanz prüfen');
    }

    // Hebel ableiten
    const levers = [];
    if (participants !== 'Einzelperson') {
      levers.push({ type: 'Abstimmung', desc: 'Gemeinsamer Projektstand reduziert Kommunikationsaufwand' });
    }
    levers.push({ type: 'Dokumentation', desc: 'Strukturierte Erfassung statt nachträglicher Rekonstruktion' });
    if (urgency === 'Erhöht') {
      levers.push({ type: 'Zeit', desc: 'Schnelle Klärung der wichtigsten offenen Punkte' });
    }
    if (pefcRelevant) {
      levers.push({ type: 'Nachweis', desc: 'PEFC-konforme Projektführung von Anfang an' });
    }

    // Reifegrad
    let maturity = 'grob';
    let maturityScore = 1;
    if (text.length > 100) maturityScore++;
    if (loc) maturityScore++;
    if (lowerText.includes('ha') || lowerText.includes('hektar')) maturityScore++;
    if (maturityScore >= 3) maturity = 'arbeitsfähig';
    if (maturityScore >= 4) maturity = 'abstimmungsfähig';

    // Wege vorschlagen
    const paths = [
      { id: 'minimal', name: 'Schneller Minimalstart', desc: 'Nur das Nötigste, schneller Einstieg', recommended: urgency === 'Erhöht' },
      { id: 'standard', name: 'Sauberer Dokumentationsstart', desc: 'Strukturierte Grundlage mit Verlauf', recommended: urgency !== 'Erhöht' && !pefcRelevant },
      { id: 'pro', name: 'Abgestimmter Profi-Start', desc: 'Mit Rollen, Nachweisen, PEFC-Bezug', recommended: pefcRelevant || participants !== 'Einzelperson' }
    ];

    return {
      caseType,
      workArea,
      participants,
      urgency,
      pefcRelevant,
      location: loc || 'Nicht angegeben',
      maturity,
      gaps: gaps.slice(0, 4),
      levers: levers.slice(0, 4),
      paths,
      nextStep: gaps.length > 0 ? `${gaps[0]} klären` : 'Projekt anlegen',
      originalInput: text
    };
  };

  const handleSelect = (key, value) => setSelections(prev => ({ ...prev, [key]: value }));
  const handleMultiSelect = (key, value) => {
    setSelections(prev => {
      const current = prev[key] || [];
      return { ...prev, [key]: current.includes(value) ? current.filter(v => v !== value) : [...current, value] };
    });
  };

  const SelectChip = ({ selected, onClick, label }) => (
    <button onClick={onClick} className={`px-3 py-2 rounded-lg border text-sm transition-all ${selected ? 'border-[#1B4D3E] bg-emerald-50 text-[#1B4D3E]' : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'}`}>
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1B4D3E] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>ForestHub</span>
          </a>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-purple-600 font-medium">KI-gestützt</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">

        {/* ========================================
            VIEW: START (AI-first Einstieg)
        ======================================== */}
        {view === 'start' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Beschreiben Sie Ihren Fall –<br />ForestHub macht daraus einen klaren Start
              </h1>
              <p className="text-stone-600 text-sm max-w-lg mx-auto">
                Geben Sie ein, was Sie haben: Stichworte, kurze Beschreibung, Notizen. 
                ForestHub verdichtet daraus Ihren Fall, zeigt offene Punkte und schlägt nächste Schritte vor.
              </p>
            </div>

            {/* Haupt-Eingabe */}
            <div className="bg-white border border-stone-200 rounded-xl p-5 mb-4">
              <label className="text-sm font-medium text-stone-700 mb-2 block">
                Was ist Ihr Vorhaben?
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="z. B. 'Jungbestandspflege auf ca. 2 ha, Eiche fördern, Birke rausnehmen, sollte dieses Jahr noch passieren, brauche noch Unternehmer...'"
                className="w-full p-4 border border-stone-200 rounded-lg text-sm focus:border-[#1B4D3E] focus:outline-none resize-none"
                rows={4}
              />
              
              {/* Zusatz-Optionen */}
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-stone-100">
                <button className="flex items-center gap-2 px-3 py-2 text-xs text-stone-500 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                  <Camera className="w-4 h-4" /> Fotos hinzufügen
                </button>
                <button className="flex items-center gap-2 px-3 py-2 text-xs text-stone-500 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                  <FileText className="w-4 h-4" /> Notiz einfügen
                </button>
                <button className="flex items-center gap-2 px-3 py-2 text-xs text-stone-500 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                  <Mic className="w-4 h-4" /> Spracheingabe
                </button>
              </div>
            </div>

            {/* Standort */}
            <div className="bg-white border border-stone-200 rounded-xl p-4 mb-6">
              <label className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-stone-400" /> Ungefährer Standort (optional)
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="z. B. Bayern, Landkreis Rosenheim"
                className="w-full p-3 border border-stone-200 rounded-lg text-sm focus:border-[#1B4D3E] focus:outline-none"
              />
            </div>

            {/* CTA */}
            <Button 
              onClick={analyzeInput}
              disabled={!input.trim()}
              className="w-full bg-[#1B4D3E] hover:bg-[#163d32] text-white h-12 text-base font-semibold disabled:opacity-50"
              data-testid="analyze-button"
            >
              <Sparkles className="mr-2 w-4 h-4" />
              Fall verdichten
            </Button>

            {/* Alternativer Pfad */}
            <div className="text-center mt-6">
              <button 
                onClick={() => setView('structured')}
                className="text-sm text-stone-500 hover:text-[#1B4D3E] transition-colors"
              >
                Ich möchte lieber strukturiert Schritt für Schritt eingeben →
              </button>
            </div>

            {/* Hinweis */}
            <p className="text-xs text-stone-400 text-center mt-8">
              ForestHub macht aus unscharfen Angaben einen ersten strukturierten Fall.<br />
              Die fachliche Verantwortung bleibt beim Menschen.
            </p>
          </div>
        )}

        {/* ========================================
            VIEW: ANALYZING
        ======================================== */}
        {view === 'analyzing' && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ForestHub verdichtet Ihren Fall...
            </h2>
            <p className="text-stone-500 text-sm">
              Falltyp erkennen · Offene Punkte identifizieren · Wege vorschlagen
            </p>
          </div>
        )}

        {/* ========================================
            VIEW: RESULT (Aha-Moment)
        ======================================== */}
        {view === 'result' && analysisResult && (
          <div>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-6 h-6 text-[#1B4D3E]" />
              </div>
              <h2 className="text-xl font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                So versteht ForestHub Ihren Fall
              </h2>
              <p className="text-sm text-stone-500">Aus Ihrer Eingabe abgeleitet – in Sekunden</p>
            </div>

            {/* Fallbild */}
            <div className="bg-white border border-stone-200 rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-stone-900 text-sm">Ihr Fallbild</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  analysisResult.maturity === 'grob' ? 'bg-amber-100 text-amber-700' :
                  analysisResult.maturity === 'arbeitsfähig' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  <Gauge className="w-3 h-3 inline mr-1" />
                  Reifegrad: {analysisResult.maturity}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-stone-50 p-3 rounded-lg">
                  <span className="text-stone-500 text-xs">Falltyp</span>
                  <p className="font-medium text-stone-900">{analysisResult.caseType}</p>
                </div>
                <div className="bg-stone-50 p-3 rounded-lg">
                  <span className="text-stone-500 text-xs">Beteiligte</span>
                  <p className="font-medium text-stone-900">{analysisResult.participants}</p>
                </div>
                <div className="bg-stone-50 p-3 rounded-lg">
                  <span className="text-stone-500 text-xs">Dringlichkeit</span>
                  <p className="font-medium text-stone-900">{analysisResult.urgency}</p>
                </div>
                <div className="bg-stone-50 p-3 rounded-lg">
                  <span className="text-stone-500 text-xs">Standort</span>
                  <p className="font-medium text-stone-900">{analysisResult.location}</p>
                </div>
              </div>
              {analysisResult.pefcRelevant && (
                <div className="mt-3 px-3 py-2 bg-emerald-50 rounded-lg text-xs text-emerald-700">
                  PEFC-/Zertifizierungsrelevanz erkannt
                </div>
              )}
            </div>

            {/* Die 3 wichtigsten offenen Punkte */}
            {analysisResult.gaps.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <h3 className="font-semibold text-amber-900 text-sm">Die wichtigsten offenen Punkte</h3>
                </div>
                <ul className="space-y-2">
                  {analysisResult.gaps.map((gap, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                      <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">{i + 1}</span>
                      {gap}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Ihre wichtigsten Hebel */}
            <div className="bg-white border border-stone-200 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <h3 className="font-semibold text-stone-900 text-sm">Ihre wahrscheinlich wichtigsten Hebel</h3>
              </div>
              <div className="space-y-2">
                {analysisResult.levers.map((lever, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium text-xs flex-shrink-0">{lever.type}</span>
                    <span className="text-stone-600">{lever.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sinnvolle Wege */}
            <div className="bg-white border border-stone-200 rounded-xl p-5 mb-4">
              <h3 className="font-semibold text-stone-900 text-sm mb-3">Drei sinnvolle Wege</h3>
              <div className="space-y-2">
                {analysisResult.paths.map((path) => (
                  <div 
                    key={path.id}
                    className={`p-3 rounded-lg border ${path.recommended ? 'border-[#1B4D3E] bg-emerald-50' : 'border-stone-200 bg-stone-50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-stone-900 text-sm">{path.name}</p>
                        <p className="text-xs text-stone-500">{path.desc}</p>
                      </div>
                      {path.recommended && (
                        <span className="px-2 py-0.5 bg-[#1B4D3E] text-white rounded text-xs">Empfohlen</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Nächster sinnvoller Schritt */}
            <div className="bg-[#1B4D3E] rounded-xl p-5 mb-6 text-white">
              <p className="text-emerald-200 text-xs uppercase tracking-wider mb-1">Nächster sinnvoller Schritt</p>
              <p className="font-semibold text-lg">{analysisResult.nextStep}</p>
            </div>

            {/* Aktionen */}
            <div className="space-y-3">
              <Button 
                onClick={onRequestDemo}
                className="w-full bg-[#1B4D3E] hover:bg-[#163d32] text-white h-12 font-semibold"
                data-testid="demo-cta-button"
              >
                <Zap className="mr-2 w-4 h-4" />
                Optimierungsprofil daraus erstellen
              </Button>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setView('structured')}
                  className="flex-1 h-11"
                >
                  <Layers className="mr-2 w-4 h-4" />
                  Fall genauer klären
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { setView('start'); setInput(''); setLocation(''); }}
                  className="flex-1 h-11"
                >
                  <RefreshCw className="mr-2 w-4 h-4" />
                  Neu starten
                </Button>
              </div>
            </div>

            {/* Hinweis */}
            <p className="text-xs text-stone-400 text-center mt-6">
              Diese Verdichtung basiert auf Ihrer Eingabe. Für ein vollständiges Optimierungsprofil 
              mit priorisierten Handlungsempfehlungen steht Ihnen die kostenpflichtige Vertiefung zur Verfügung.
            </p>
          </div>
        )}

        {/* ========================================
            VIEW: STRUCTURED (7-W Fallback)
        ======================================== */}
        {view === 'structured' && (
          <div>
            <button 
              onClick={() => setView(analysisResult ? 'result' : 'start')}
              className="flex items-center gap-2 text-sm text-stone-500 hover:text-[#1B4D3E] mb-6"
            >
              <ArrowLeft className="w-4 h-4" /> Zurück
            </button>

            <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fall Schritt für Schritt klären
            </h2>
            <p className="text-sm text-stone-500 mb-6">
              Strukturierte Eingabe nach der 7-W-Logik für präzisere Ergebnisse
            </p>

            {/* Kompakte 7-W Eingabe */}
            <div className="space-y-4">
              {/* Wer */}
              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-700 mb-2">1. Wer ist beteiligt?</p>
                <div className="flex flex-wrap gap-2">
                  {['Waldbesitzer', 'Forstbetrieb', 'Unternehmer', 'Förster'].map(opt => (
                    <SelectChip key={opt} selected={selections.targetGroup === opt} onClick={() => handleSelect('targetGroup', opt)} label={opt} />
                  ))}
                </div>
              </div>

              {/* Wo */}
              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-700 mb-2">2. Wo liegt das Vorhaben?</p>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Bundesland" value={selections.bundesland} onChange={(e) => handleSelect('bundesland', e.target.value)} className="p-2 border border-stone-200 rounded text-sm" />
                  <input type="text" placeholder="Landkreis" value={selections.landkreis} onChange={(e) => handleSelect('landkreis', e.target.value)} className="p-2 border border-stone-200 rounded text-sm" />
                </div>
              </div>

              {/* Was */}
              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-700 mb-2">3. Was ist das Vorhaben?</p>
                <div className="flex flex-wrap gap-2">
                  {['Pflanzung', 'Jungbestandspflege', 'Durchforstung', 'Holzernte', 'Waldschutz'].map(opt => (
                    <SelectChip key={opt} selected={selections.workArea === opt} onClick={() => handleSelect('workArea', opt)} label={opt} />
                  ))}
                </div>
              </div>

              {/* Wann */}
              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-700 mb-2">4. Wann?</p>
                <div className="flex flex-wrap gap-2">
                  {['Dringend', 'Nächste Wochen', 'Saisonal', 'Offen'].map(opt => (
                    <SelectChip key={opt} selected={selections.timing === opt} onClick={() => handleSelect('timing', opt)} label={opt} />
                  ))}
                </div>
              </div>

              {/* Wie */}
              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-700 mb-2">5. Wie starten?</p>
                <div className="flex flex-wrap gap-2">
                  {['Minimal', 'Standard', 'Profi'].map(opt => (
                    <SelectChip key={opt} selected={selections.startLevel === opt} onClick={() => handleSelect('startLevel', opt)} label={opt} />
                  ))}
                </div>
              </div>

              {/* PEFC */}
              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-700 mb-2">6. PEFC relevant?</p>
                <div className="flex flex-wrap gap-2">
                  {['Ja', 'Vielleicht', 'Nein', 'Unklar'].map(opt => (
                    <SelectChip key={opt} selected={selections.pefcRelevant === opt} onClick={() => handleSelect('pefcRelevant', opt)} label={opt} />
                  ))}
                </div>
              </div>
            </div>

            <Button 
              onClick={() => {
                // Aus strukturierter Eingabe Analyse generieren
                const structuredInput = `${selections.workArea || ''} ${selections.targetGroup || ''} ${selections.timing || ''} ${selections.pefcRelevant === 'Ja' ? 'PEFC' : ''}`;
                const result = generateAnalysis(structuredInput, selections.bundesland);
                setAnalysisResult(result);
                setView('result');
              }}
              className="w-full bg-[#1B4D3E] hover:bg-[#163d32] text-white h-12 mt-6 font-semibold"
            >
              Vorschlag erstellen
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}

      </main>

      <footer className="border-t border-stone-200 py-6 bg-white mt-12">
        <div className="max-w-3xl mx-auto px-6 text-center text-sm text-stone-500">
          <a href="/" className="hover:text-[#1B4D3E] transition-colors">← Zurück zur Startseite</a>
        </div>
      </footer>
    </div>
  );
};
